/**
 * Cookie Consent Utilities
 * Helper functions to manage cookie consent across the application
 */

export interface CookieConsent {
    analytics: boolean;
    marketing: boolean;
    essential: boolean;
}

/**
 * Global Window type declarations for cookie-related scripts
 */
declare global {
    interface Window {
        dataLayer?: any[];
        gtag?: (...args: any[]) => void;
        _fbq?: any;
        fbq?: (...args: any[]) => void;
    }
}

/**
 * Get the current cookie consent settings
 */
export const getCookieConsent = (): CookieConsent | null => {
    try {
        const consent = localStorage.getItem('cookieConsent');
        return consent ? JSON.parse(consent) : null;
    } catch (error) {
        console.error('Failed to parse cookie consent from localStorage:', error);
        // Return null if parsing fails, consider clearing the bad key
        try {
            localStorage.removeItem('cookieConsent');
        } catch (e) {
            // Ignore if removal fails
        }
        return null;
    }
};

/**
 * Check if a specific type of cookie is accepted
 */
export const isCookieAccepted = (cookieType: keyof CookieConsent): boolean => {
    const consent = getCookieConsent();
    return consent ? consent[cookieType] : false;
};

/**
 * Check if analytics cookies are accepted
 */
export const isAnalyticsCookieAccepted = (): boolean => {
    return isCookieAccepted('analytics');
};

/**
 * Check if marketing cookies are accepted
 */
export const isMarketingCookieAccepted = (): boolean => {
    return isCookieAccepted('marketing');
};

/**
 * Get when the consent was given (ISO timestamp)
 */
export const getCookieConsentTimestamp = (): string | null => {
    return localStorage.getItem('cookieConsentTimestamp');
};

/**
 * Check if user has given any consent
 */
export const hasUserConsentedToCookies = (): boolean => {
    return localStorage.getItem('cookieConsent') !== null;
};

/**
 * Load Google Analytics script (example)
 * Call this after user accepts analytics cookies
 */
export const loadGoogleAnalytics = (trackingId: string) => {
    // Check if analytics cookies are accepted
    if (!isAnalyticsCookieAccepted()) {
        return;
    }

    // Prevent duplicate script injection
    const scripts = document.querySelectorAll(`script[src*="googletagmanager.com/gtag"]`);
    if (scripts.length > 0) {
        return;
    }

    // Add Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
    document.head.appendChild(script);

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];

    // Define gtag function using rest parameter
    function gtag(...args: any[]) {
        window.dataLayer.push(args);
    }
    window.gtag = gtag;

    gtag('js', new Date());
    gtag('config', trackingId, {
        'anonymize_ip': true,
        'allow_google_signals': false,
    });
};

/**
 * Load Facebook Pixel script (example)
 * Call this after user accepts marketing cookies
 */
export const loadFacebookPixel = (pixelId: string) => {
    // Check if marketing cookies are accepted
    if (!isMarketingCookieAccepted()) {
        return;
    }

    // Validate pixelId against strict pattern (numbers only)
    const pixelIdPattern = /^\d+$/;
    if (!pixelIdPattern.test(pixelId)) {
        console.error('Invalid Facebook Pixel ID format');
        return;
    }

    // Prevent duplicate script injection
    if (window.fbq || window._fbq) {
        return;
    }

    // Load the external fbevents.js library
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';

    script.onload = () => {
        if (window.fbq) {
            window.fbq('init', pixelId);
            window.fbq('track', 'PageView');
        }
    };

    document.head.appendChild(script);
};

/**
 * Load analytics scripts based on stored preferences
 * Call this when analytics cookies are accepted
 */
export const loadAnalyticsScripts = () => {
    if (!isAnalyticsCookieAccepted()) {
        return;
    }

    // Example: Load Google Analytics if tracking ID is available
    const trackingId = process.env.REACT_APP_GA_TRACKING_ID;
    if (trackingId) {
        loadGoogleAnalytics(trackingId);
    }
};

/**
 * Load marketing scripts based on stored preferences
 * Call this when marketing cookies are accepted
 */
export const loadMarketingScripts = () => {
    if (!isMarketingCookieAccepted()) {
        return;
    }

    // Example: Load Facebook Pixel if pixel ID is available
    const pixelId = process.env.REACT_APP_FB_PIXEL_ID;
    if (pixelId) {
        loadFacebookPixel(pixelId);
    }
};

/**
 * Revoke cookie consent
 */
export const revokeCookieConsent = () => {
    localStorage.removeItem('cookieConsent');
    localStorage.removeItem('cookieConsentTimestamp');
};
